import { FileText, Film, ImagePlus, Link2, Loader2, Plus, Sparkles, Trash2 } from "lucide-react";
import { ResilientImage } from "@/components/media/ResilientImage";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LearnMore } from "@/components/resources/LearnMore";
import { toast } from "sonner";

import { PedigreeReviewDialog } from "@/components/PedigreeReviewDialog";
import { PerformanceRelativesEditor } from "@/components/PerformanceRelativesEditor";
import { VideoManager } from "@/components/video/VideoManager";
import { errorInputCls, FieldError, useFieldErrors } from "@/components/forms/useFieldErrors";
import { AmountInput } from "@/components/ui/amount-input";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { api, assetUrl } from "@/lib/api";
import { ACCEPT_DOCS, ACCEPT_DOCS_AND_VIDEO, ACCEPT_PHOTOS, ACCEPT_VIDEOS, inferKind, uploadErrorMessage } from "@/lib/uploads";
import { VIDEO_CATEGORIES } from "@/config/media";

const inputCls = "h-11 rounded-xl border-black/10 bg-transparent";

const ASSET_TYPES = ["sport_horse", "young_horse", "foal", "yearling", "stallion", "broodmare", "embryo", "breeding_right", "semen_dose"];
const GENETIC_TYPES = ["embryo", "breeding_right", "semen_dose"];
// keep in sync with backend horses/models.py DISCIPLINES
const DISCIPLINES = ["show_jumping", "dressage", "eventing", "racing", "morphology", "thoroughbred_racing", "polo", "trotters"];
// General documents (media.category on kind=document) — keep in sync with backend DOC_CATEGORIES
const DOC_CATEGORIES = ["registration_papers", "passport", "general"];
// Veterinary Repository categories — keep in sync with backend VET_CATEGORIES
const VET_CATEGORIES = ["radiographs", "veterinary_reports", "endoscopy", "ultrasounds", "clinical_examination", "blood_tests", "certificates", "additional_documents"];

/**
 * Reusable horse creation/edit form with inline photo upload.
 * Used in the wizard and in My Horses.
 */
export const HorseForm = ({ initial, onSaved, onCancel, compact = false }) => {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const aiFileRef = useRef(null);
  const { errors, validate, clearField } = useFieldErrors();
  const [form, setForm] = useState({
    name: initial?.name || "",
    asset_type: initial?.asset_type || "sport_horse",
    expected_birth_date: initial?.expected_birth_date || "",
    embryo_state: initial?.embryo_state || "",
    breeder: initial?.breeder || "",
    year_of_birth: initial?.year_of_birth || "",
    sex: initial?.sex || "",
    studbook: initial?.studbook || "",
    color: initial?.color || "",
    height_cm: initial?.height_cm || "",
    disciplines: initial?.disciplines || [],
    description: initial?.description?.en || "",
    sire: initial?.pedigree?.sire?.name || "",
    dam: initial?.pedigree?.dam?.name || "",
  });
  const [results, setResults] = useState(initial?.results || []);
  // Fase C — Performance Relatives (líneas materna/paterna, modelo único)
  const [relatives, setRelatives] = useState(initial?.performance_relatives || []);
  const [studbookRefs, setStudbookRefs] = useState(initial?.studbook_refs || {});
  // full generational tree (AI import / previous saves) — sire/dam inputs stay in sync
  const [pedigreeTree, setPedigreeTree] = useState(initial?.pedigree || null);
  const [mediaIds, setMediaIds] = useState(initial?.media_ids || []);
  const [previews, setPreviews] = useState([]);
  const [extLinks, setExtLinks] = useState(initial?.external_links || []);
  const [docs, setDocs] = useState([]);
  const [vetItems, setVetItems] = useState([]); // Veterinary Repository: {id, name, vet_category}
  const [sireMedia, setSireMedia] = useState([]);
  const [damMedia, setDamMedia] = useState([]);
  // unified video system (Block 4 item 4): ordered mixed-source assets + starred main
  const [videos, setVideos] = useState([]);
  const [mainVideoId, setMainVideoId] = useState(initial?.main_video_id || null);
  const [videoUploadsBusy, setVideoUploadsBusy] = useState(false);
  const docRef = useRef(null);
  const vetRef = useRef(null);
  const vetCategoryRef = useRef("veterinary_reports"); // category armed before the picker opens
  const sireRef = useRef(null);
  const damRef = useRef(null);
  const [uploadPct, setUploadPct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiResult, setAiResult] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target?.value ?? e }));
  const toggleDiscipline = (d) =>
    setForm((f) => ({
      ...f,
      disciplines: f.disciplines.includes(d) ? f.disciplines.filter((x) => x !== d) : [...f.disciplines, d],
    }));
  const genetic = GENETIC_TYPES.includes(form.asset_type);

  /* ---------- organizer doc & parental media management: load existing when editing ---------- */
  useEffect(() => {
    if (!initial?.id) return;
    const ids = new Set(initial.media_ids || []);
    const vetIds = new Set(initial.vet_report_media_ids || []);
    const sireIds = initial.sire_info?.video_media_ids || [];
    const damIds = initial.dam_info?.video_media_ids || [];
    api
      .get("/media")
      .then(({ data }) => {
        const byId = Object.fromEntries(data.map((m) => [m.id, m]));
        setDocs(
          data
            .filter((m) => ids.has(m.id) && m.kind === "document")
            .map((m) => ({ id: m.id, name: m.original_filename, category: m.category || "general" }))
        );
        setVetItems(
          data
            .filter((m) => vetIds.has(m.id))
            .map((m) => ({ id: m.id, name: m.original_filename, vet_category: m.vet_category || "veterinary_reports" }))
        );
        const toClip = (id) => byId[id] && { id, name: byId[id].original_filename, category: byId[id].category };
        setSireMedia(sireIds.map(toClip).filter(Boolean));
        setDamMedia(damIds.map(toClip).filter(Boolean));
        // ordered mixed-source videos (uploads | YouTube | Vimeo)
        setVideos((initial.video_ids || []).map((id) => byId[id]).filter(Boolean));
      })
      .catch(() => {});
  }, [initial?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDocUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", "document");
        fd.append("category", "general");
        const { data } = await api.post("/media/upload", fd);
        setMediaIds((ids) => [...ids, data.id]);
        setDocs((d) => [...d, { id: data.id, name: file.name, category: "general" }]);
      } catch (err) {
        setError(uploadErrorMessage(err, t));
      }
    }
    if (docRef.current) docRef.current.value = "";
  };

  const setDocCategory = async (id, category) => {
    await api.patch(`/media/${id}`, { category });
    setDocs((d) => d.map((x) => (x.id === id ? { ...x, category } : x)));
  };

  const removeDoc = async (id) => {
    await api.delete(`/media/${id}`);
    setDocs((d) => d.filter((x) => x.id !== id));
    setMediaIds((ids) => ids.filter((x) => x !== id));
  };

  /* ---------- Veterinary Repository: structured professional archive (optional) ---------- */
  const handleVetUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    const category = vetCategoryRef.current;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", "document");
        fd.append("vet_category", category);
        const { data } = await api.post("/media/upload", fd);
        setVetItems((v) => [...v, { id: data.id, name: file.name, vet_category: category }]);
      } catch (err) {
        setError(uploadErrorMessage(err, t));
      }
    }
    if (vetRef.current) vetRef.current.value = "";
  };

  const setVetCategory = async (id, vet_category) => {
    await api.patch(`/media/${id}`, { vet_category });
    setVetItems((v) => v.map((x) => (x.id === id ? { ...x, vet_category } : x)));
  };

  const removeVetItem = async (id) => {
    await api.delete(`/media/${id}`);
    setVetItems((v) => v.filter((x) => x.id !== id));
  };

  /* ---------- B3: parental (sire/dam) video management for embryos, foals & yearlings ---------- */
  const parental = genetic || ["foal", "yearling", "young_horse"].includes(form.asset_type);

  const handleParentUpload = (which) => async (e) => {
    const files = Array.from(e.target.files || []);
    const setter = which === "sire" ? setSireMedia : setDamMedia;
    for (const file of files) {
      setUploadPct(0);
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", "video");
        const { data } = await api.post("/media/upload", fd, {
          onUploadProgress: (ev) => setUploadPct(Math.round((ev.loaded / (ev.total || 1)) * 100)),
        });
        setter((ms) => [...ms, { id: data.id, name: file.name, category: null }]);
      } catch (err) {
        setError(uploadErrorMessage(err, t));
      }
    }
    setUploadPct(null);
    const ref = which === "sire" ? sireRef : damRef;
    if (ref.current) ref.current.value = "";
  };

  const setParentClipCategory = (which) => async (id, category) => {
    await api.patch(`/media/${id}`, { category: category || null });
    const setter = which === "sire" ? setSireMedia : setDamMedia;
    setter((ms) => ms.map((m) => (m.id === id ? { ...m, category } : m)));
  };

  const removeParentClip = (which) => (id) => {
    const setter = which === "sire" ? setSireMedia : setDamMedia;
    setter((ms) => ms.filter((m) => m.id !== id)); // unlink only — asset stays in the media library
  };

  /* preserves existing ancestor fields (results, offspring...) — only name & clips change here */
  const buildAncestor = (which) => {
    if (!parental) return undefined;
    const name = which === "sire" ? form.sire : form.dam;
    const existing = initial?.[`${which}_info`] || {};
    const clips = which === "sire" ? sireMedia : damMedia;
    if (!name && !existing.name && clips.length === 0) return undefined;
    return { ...existing, name: name || existing.name || "\u2014", video_media_ids: clips.map((m) => m.id) };
  };

  const handleUpload = async (e, source) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      setUploadPct(0);
      try {
        // inferKind handles files the browser reports with an EMPTY mime type
        // (typical for .heic/.mov on machines without associations) so photos
        // never get misfiled as documents (client review item 14).
        const kind = inferKind(file, source);
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", kind);
        const { data } = await api.post("/media/upload", fd, {
          onUploadProgress: (ev) => setUploadPct(Math.round((ev.loaded / (ev.total || 1)) * 100)),
        });
        setMediaIds((ids) => [...ids, data.id]);
        setPreviews((p) => [...p, { id: data.id, url: assetUrl(data), kind, category: data.category || null, name: file.name }]);
      } catch (err) {
        setError(uploadErrorMessage(err, t));
      }
    }
    setUploadPct(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  /* ---------- AI improve: polish the sale description in place ---------- */
  const [improving, setImproving] = useState(false);
  const improveDescription = async () => {
    if (!form.description.trim() || improving) return;
    setImproving(true);
    try {
      const { data } = await api.post("/ai/improve", { text: form.description, locale: "en" }, { timeout: 120000 });
      setForm((f) => ({ ...f, description: data.text }));
      toast.success(t("ai.improvedToast"));
    } catch {
      toast.error(t("ai.failed"));
    } finally {
      setImproving(false);
    }
  };

  /* ---------- AI pedigree import: upload → extraction → review (never auto-save) ---------- */
  const handleAiImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/horses/pedigree/extract", fd, { timeout: 120000 });
      setAiResult(data);
      setReviewOpen(true);
    } catch (err) {
      const code = err.response?.data?.detail;
      setAiError(code === "file_too_large" ? t("ai.fileTooLarge") : code === "unsupported_type" ? t("ai.unsupportedType") : t("ai.failedDesc"));
    } finally {
      setAiLoading(false);
      if (aiFileRef.current) aiFileRef.current.value = "";
    }
  };

  const handleAiApply = ({ fields, registration_ids, pedigree, description }) => {
    setForm((f) => ({
      ...f,
      name: f.name || fields.name || "",
      year_of_birth: fields.year_of_birth || f.year_of_birth,
      studbook: fields.studbook || f.studbook,
      color: fields.color || f.color,
      breeder: fields.breeder || f.breeder,
      sire: pedigree?.sire?.name || f.sire,
      dam: pedigree?.dam?.name || f.dam,
      description: description || f.description,
    }));
    if (registration_ids && Object.keys(registration_ids).length > 0) setStudbookRefs(registration_ids);
    if (pedigree) setPedigreeTree(pedigree);
    toast.success(t("ai.appliedToast"));
  };

  /* sire/dam inputs merge into the deep tree so AI-imported generations survive */
  const buildPedigree = () => {
    if (!pedigreeTree && !form.sire && !form.dam) return undefined;
    const base = pedigreeTree ? JSON.parse(JSON.stringify(pedigreeTree)) : {};
    base.name = form.name || base.name || "—";
    if (form.sire) base.sire = { ...(base.sire || {}), name: form.sire };
    else if (!base.sire?.name) delete base.sire;
    if (form.dam) base.dam = { ...(base.dam || {}), name: form.dam };
    else if (!base.dam?.name) delete base.dam;
    return base.sire || base.dam ? base : undefined;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.disciplines.length === 0) {
      setError(t("horse.disciplinesRequired"));
      return;
    }
    // C4: para embriones, congelado/implantado es OBLIGATORIO (validación
    // visual con el patrón useFieldErrors)
    if (
      !validate({
        embryo_state:
          form.asset_type === "embryo" && !form.embryo_state ? t("horse.embryoStateRequired") : null,
      })
    ) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: form.name,
        asset_type: form.asset_type,
        expected_birth_date: form.expected_birth_date || undefined,
        embryo_state: form.asset_type === "embryo" ? form.embryo_state : undefined,
        breeder: form.breeder || undefined,
        sire_info: buildAncestor("sire"),
        dam_info: buildAncestor("dam"),
        year_of_birth: form.year_of_birth ? Number(form.year_of_birth) : undefined,
        sex: form.sex || undefined,
        studbook: form.studbook || undefined,
        color: form.color || undefined,
        height_cm: form.height_cm ? Number(form.height_cm) : undefined,
        disciplines: form.disciplines,
        description: form.description ? { ...(initial?.description || {}), en: form.description } : {},
        media_ids: mediaIds,
        cover_media_id: mediaIds[0] || undefined,
        video_ids: videos.map((v) => v.id),
        // "" clears the star on PATCH (backend drops nulls); omit entirely on create
        main_video_id: mainVideoId || (initial?.id ? "" : undefined),
        vet_report_media_ids: vetItems.map((v) => v.id),
        results: results.filter((r) => r.competition && r.result).map((r) => ({ ...r, year: r.year ? Number(r.year) : undefined })),
        performance_relatives: relatives
          .filter((r) => (r.name || "").trim())
          .map((r) => ({
            line: r.line,
            relationship: r.relationship || "other",
            name: r.name.trim(),
            sire: r.sire?.trim() || undefined,
            dam: r.dam?.trim() || undefined,
            discipline: r.discipline || undefined,
            level: r.level?.trim() || undefined,
            result: r.result?.trim() || undefined,
            url: r.url?.trim() || undefined,
          })),
        external_links: extLinks.filter((l) => l.label && l.url),
        studbook_refs: Object.keys(studbookRefs).length > 0 ? studbookRefs : undefined,
        pedigree: buildPedigree(),
      };
      const { data } = initial?.id
        ? await api.patch(`/horses/${initial.id}`, payload)
        : await api.post("/horses", payload);
      onSaved(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : t("auth.genericError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5" data-testid="horse-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5 sm:col-span-2">
          <Label htmlFor="hf-name">{t("horse.name")}</Label>
          <Input id="hf-name" data-testid="horse-name-input" required value={form.name} onChange={set("name")} placeholder={t("horse.namePh")} className={inputCls} />
        </div>
        <div className="flex flex-col gap-1.5 sm:col-span-2">
          <Label htmlFor="hf-asset-type">{t("horse.assetType")}</Label>
          <Select value={form.asset_type} onValueChange={(v) => setForm((f) => ({ ...f, asset_type: v }))}>
            <SelectTrigger id="hf-asset-type" data-testid="horse-asset-type-select" className={inputCls}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              {ASSET_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {t(`asset.${type}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {form.asset_type === "embryo" && (
          <>
            {/* C4: obligatorio — congelado o implantado */}
            <div className="flex flex-col gap-1.5" data-field="embryo_state">
              <Label htmlFor="hf-embryo-state">{t("horse.embryoState")}</Label>
              <Select
                value={form.embryo_state}
                onValueChange={(v) => {
                  setForm((f) => ({ ...f, embryo_state: v }));
                  clearField("embryo_state");
                }}
              >
                <SelectTrigger
                  id="hf-embryo-state"
                  data-testid="horse-embryo-state-select"
                  aria-invalid={Boolean(errors.embryo_state)}
                  className={`${inputCls} ${errors.embryo_state ? errorInputCls : ""}`}
                >
                  <SelectValue placeholder={t("horse.embryoStatePh")} />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="frozen">{t("horse.embryoFrozen")}</SelectItem>
                  <SelectItem value="implanted">{t("horse.embryoImplanted")}</SelectItem>
                </SelectContent>
              </Select>
              <FieldError field="embryo_state" message={errors.embryo_state} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="hf-ebd">{t("horse.expectedBirth")}</Label>
              <DatePicker id="hf-ebd" data-testid="horse-expected-birth-input" value={form.expected_birth_date} onChange={(v) => setForm((f) => ({ ...f, expected_birth_date: v }))} className={inputCls} />
            </div>
          </>
        )}
        {!genetic && (
          <>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="hf-yob">{t("horse.yob")}</Label>
              <AmountInput id="hf-yob" data-testid="horse-yob-input" grouping={false} value={form.year_of_birth} onValueChange={(v) => setForm((f) => ({ ...f, year_of_birth: v }))} className={inputCls} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="hf-sex">{t("horse.sex")}</Label>
              <Select value={form.sex} onValueChange={(v) => setForm((f) => ({ ...f, sex: v }))}>
                <SelectTrigger id="hf-sex" data-testid="horse-sex-select" className={inputCls}>
                  <SelectValue placeholder={t("horse.sex")} />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="stallion">{t("horse.stallion")}</SelectItem>
                  <SelectItem value="mare">{t("horse.mare")}</SelectItem>
                  <SelectItem value="gelding">{t("horse.gelding")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        )}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="hf-studbook">{t("horse.studbook")}</Label>
          <Input id="hf-studbook" data-testid="horse-studbook-input" value={form.studbook} onChange={set("studbook")} placeholder={t("horse.studbookPh")} className={inputCls} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="hf-breeder">{t("horse.breeder")}</Label>
          <Input id="hf-breeder" data-testid="horse-breeder-input" value={form.breeder} onChange={set("breeder")} placeholder={t("horse.breederPh")} className={inputCls} />
          {initial?.breed_legacy && (
            <span className="text-[11px] text-[#737373]" data-testid="horse-breed-legacy-hint">
              {t("horse.breedLegacy")}: {initial.breed_legacy}
            </span>
          )}
        </div>
        {!genetic && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="hf-height">{t("horse.height")}</Label>
            <AmountInput id="hf-height" data-testid="horse-height-input" grouping={false} value={form.height_cm} onValueChange={(v) => setForm((f) => ({ ...f, height_cm: v }))} className={inputCls} />
          </div>
        )}
        {/* ---------- AI pedigree import ---------- */}
        <div className="sm:col-span-2 rounded-2xl border border-dashed border-[#1D4D3B]/30 bg-[#1D4D3B]/[0.03] p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1 flex flex-col gap-0.5">
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#0A0A0A]">
              <Sparkles className="w-4 h-4 text-[#1D4D3B]" aria-hidden="true" />
              {t("ai.importPedigree")}
            </span>
            <span className="text-xs text-[#737373]">{t("ai.importHint")}</span>
            <LearnMore slug="creating-great-horse-profiles" section="pedigree-and-competition-records" className="w-fit pt-0.5" />
            {aiError && (
              <span className="text-xs text-red-700 pt-1" role="alert" data-testid="ai-import-error">
                {t("ai.failedTitle")} — {aiError}
              </span>
            )}
          </div>
          <input ref={aiFileRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={handleAiImport} data-testid="ai-import-input" />
          <Button
            type="button"
            data-testid="ai-import-button"
            onClick={() => aiFileRef.current?.click()}
            disabled={aiLoading}
            className="rounded-full h-10 px-5 bg-[#1D4D3B] text-white hover:bg-[#1D4D3B]/90 transition-colors duration-300 disabled:opacity-60 shrink-0"
          >
            {aiLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
                {t("ai.reading")}
              </>
            ) : (
              t("ai.uploadDocument")
            )}
          </Button>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="hf-sire">{t("horse.sire")}</Label>
          <Input id="hf-sire" data-testid="horse-sire-input" value={form.sire} onChange={set("sire")} placeholder={t("horse.sirePh")} className={inputCls} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="hf-dam">{t("horse.dam")}</Label>
          <Input id="hf-dam" data-testid="horse-dam-input" value={form.dam} onChange={set("dam")} placeholder={t("horse.damPh")} className={inputCls} />
        </div>
        {!compact && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="hf-color">{t("horse.color")}</Label>
            <Input id="hf-color" data-testid="horse-color-input" value={form.color} onChange={set("color")} className={inputCls} />
          </div>
        )}
        <div className="flex flex-col gap-2 sm:col-span-2">
          <Label>{t("horse.disciplines")}</Label>
          <div className="flex flex-wrap gap-2" role="group" aria-label={t("horse.disciplines")}>
            {DISCIPLINES.map((d) => (
              <button
                key={d}
                type="button"
                data-testid={`horse-discipline-chip-${d}`}
                aria-pressed={form.disciplines.includes(d)}
                onClick={() => toggleDiscipline(d)}
                className={`px-4 h-9 rounded-full text-sm font-medium transition-colors duration-200 ${
                  form.disciplines.includes(d)
                    ? "bg-[#0A0A0A] text-white"
                    : "bg-black/5 text-[#0A0A0A]/60 hover:text-[#0A0A0A]"
                }`}
              >
                {t(`disc.${d}`)}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-1.5 sm:col-span-2">
          <Label htmlFor="hf-desc">{t("horse.description")}</Label>
          <Textarea id="hf-desc" data-testid="horse-description-input" rows={3} value={form.description} onChange={set("description")} placeholder={t("horse.descPh")} className="rounded-xl border-black/10 bg-transparent" />
          {form.description.trim() !== "" && (
            <button
              type="button"
              onClick={improveDescription}
              disabled={improving}
              data-testid="horse-improve-description"
              className="self-end inline-flex items-center gap-1.5 text-[11px] font-medium text-[#1D4D3B] hover:opacity-70 transition-opacity duration-200 disabled:opacity-40"
            >
              {improving ? <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" /> : <Sparkles className="w-3 h-3" aria-hidden="true" />}
              {improving ? t("ai.working") : t("ai.improve")}
            </button>
          )}
        </div>
      </div>

      {!compact && (
        <div className="flex flex-col gap-3">
          <Label>{t("horse.resultsSection")}</Label>
          {results.map((r, i) => (
            <div key={i} className="grid grid-cols-[80px_1fr_1fr_36px] gap-2 items-center" data-testid={`horse-result-row-${i}`}>
              <AmountInput
                aria-label={t("horse.yob")}
                grouping={false}
                value={r.year || ""}
                placeholder={t("horse.yearPh")}
                onValueChange={(v) => setResults((rs) => rs.map((x, j) => (j === i ? { ...x, year: v } : x)))}
                className="h-10 rounded-xl border-black/10"
              />
              <Input
                aria-label={t("horse.competitionPh")}
                value={r.competition || ""}
                placeholder={t("horse.competitionPh")}
                onChange={(e) => setResults((rs) => rs.map((x, j) => (j === i ? { ...x, competition: e.target.value } : x)))}
                className="h-10 rounded-xl border-black/10"
              />
              <Input
                aria-label={t("horse.resultPh")}
                value={r.result || ""}
                placeholder={t("horse.resultPh")}
                onChange={(e) => setResults((rs) => rs.map((x, j) => (j === i ? { ...x, result: e.target.value } : x)))}
                className="h-10 rounded-xl border-black/10"
              />
              <button
                type="button"
                data-testid={`horse-result-remove-${i}`}
                aria-label={t("horse.removeResult")}
                onClick={() => setResults((rs) => rs.filter((_, j) => j !== i))}
                className="h-9 w-9 rounded-full flex items-center justify-center text-[#0A0A0A]/30 hover:text-red-700 hover:bg-red-50 transition-colors duration-200"
              >
                <Trash2 className="w-4 h-4" aria-hidden="true" />
              </button>
            </div>
          ))}
          <button
            type="button"
            data-testid="horse-result-add"
            onClick={() => setResults((rs) => [...rs, { year: "", competition: "", result: "" }])}
            className="w-fit inline-flex items-center gap-2 text-sm font-medium text-[#0A0A0A]/60 hover:text-[#0A0A0A] transition-colors duration-200"
          >
            <Plus className="w-4 h-4" aria-hidden="true" />
            {t("horse.addResult")}
          </button>
        </div>
      )}

      {!compact && <PerformanceRelativesEditor value={relatives} onChange={setRelatives} />}

      {!compact && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-0.5">
            <Label>{t("horse.externalLinks")}</Label>
            <span className="text-xs text-[#737373]">{t("horse.externalLinksHint")}</span>
          </div>
          {extLinks.map((l, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_1.4fr_36px] gap-2 items-center" data-testid={`horse-extlink-row-${i}`}>
              <Input
                aria-label={t("horse.linkLabelPh")}
                value={l.label || ""}
                placeholder={t("horse.linkLabelPh")}
                onChange={(e) => setExtLinks((ls) => ls.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                className="h-10 rounded-xl border-black/10"
              />
              <Input
                aria-label={t("horse.linkUrlPh")}
                type="url"
                value={l.url || ""}
                placeholder={t("horse.linkUrlPh")}
                onChange={(e) => setExtLinks((ls) => ls.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)))}
                className="h-10 rounded-xl border-black/10"
              />
              <button
                type="button"
                data-testid={`horse-extlink-remove-${i}`}
                aria-label={t("horse.removeLink")}
                onClick={() => setExtLinks((ls) => ls.filter((_, j) => j !== i))}
                className="h-9 w-9 rounded-full flex items-center justify-center text-[#0A0A0A]/30 hover:text-red-700 hover:bg-red-50 transition-colors duration-200"
              >
                <Trash2 className="w-4 h-4" aria-hidden="true" />
              </button>
            </div>
          ))}
          <button
            type="button"
            data-testid="horse-extlink-add"
            onClick={() => setExtLinks((ls) => [...ls, { label: "", url: "" }])}
            className="w-fit inline-flex items-center gap-2 text-sm font-medium text-[#0A0A0A]/60 hover:text-[#0A0A0A] transition-colors duration-200"
          >
            <Link2 className="w-4 h-4" aria-hidden="true" />
            {t("horse.addLink")}
          </button>
        </div>
      )}

      {!compact && parental && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-0.5">
            <Label>{t("horse.parentalMedia")}</Label>
            <span className="text-xs text-[#737373]">{t("horse.parentalMediaHint")}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              ["sire", sireMedia, sireRef],
              ["dam", damMedia, damRef],
            ].map(([which, clips, ref]) => (
              <div key={which} className="rounded-2xl border border-black/5 bg-[#F9F9F9] p-4 flex flex-col gap-3" data-testid={`parental-media-${which}`}>
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0A0A0A]/40">
                  {t(`horse.${which}`)}
                  {(which === "sire" ? form.sire : form.dam) && (
                    <span className="ml-2 normal-case tracking-normal font-medium text-[#0A0A0A]">
                      {which === "sire" ? form.sire : form.dam}
                    </span>
                  )}
                </span>
                {clips.map((m) => (
                  <div key={m.id} className="flex items-center gap-2 rounded-xl border border-black/5 bg-white px-3 py-2" data-testid={`parental-clip-${m.id}`}>
                    <Film className="w-4 h-4 text-black/30 shrink-0" aria-hidden="true" />
                    <span className="text-[11px] font-medium text-[#0A0A0A] truncate min-w-0 flex-1">{m.name}</span>
                    <Select value={m.category || "none"} onValueChange={(v) => setParentClipCategory(which)(m.id, v === "none" ? "" : v)}>
                      <SelectTrigger
                        data-testid={`parental-clip-category-${m.id}`}
                        aria-label={t("med.category")}
                        className="h-6 w-auto rounded-full bg-black/5 text-[#0A0A0A] text-[11px] px-2 border-0 shadow-none gap-1 shrink-0 focus:ring-0"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-xl">
                        <SelectItem value="none">{t("med.uncategorized")}</SelectItem>
                        {VIDEO_CATEGORIES.map((c) => (
                          <SelectItem key={c} value={c}>
                            {t(`vidcat.${c}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <button
                      type="button"
                      data-testid={`parental-clip-remove-${m.id}`}
                      aria-label={t("horse.unlinkVideo")}
                      onClick={() => removeParentClip(which)(m.id)}
                      className="h-7 w-7 rounded-full flex items-center justify-center text-[#0A0A0A]/30 hover:text-red-700 hover:bg-red-50 transition-colors duration-200 shrink-0"
                    >
                      <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                    </button>
                  </div>
                ))}
                <input ref={ref} type="file" multiple accept={ACCEPT_VIDEOS} className="hidden" onChange={handleParentUpload(which)} data-testid={`parental-video-input-${which}`} />
                <button
                  type="button"
                  data-testid={`parental-video-upload-${which}`}
                  onClick={() => ref.current?.click()}
                  disabled={uploadPct !== null}
                  className="w-fit inline-flex items-center gap-2 text-sm font-medium text-[#0A0A0A]/60 hover:text-[#0A0A0A] transition-colors duration-200 disabled:opacity-50"
                >
                  {uploadPct !== null ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Plus className="w-4 h-4" aria-hidden="true" />}
                  {t("horse.addVideo")}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---------- separated media sections (Phase 3.5) — each independent & optional ---------- */}
      <div className="flex flex-col gap-3" data-testid="horse-photos-section">
        <div className="flex items-center gap-2">
          <Label>{t("horse.photosSection")}</Label>
          <span className="text-[11px] text-[#737373]">{t("horse.optionalSection")}</span>
          <LearnMore slug="creating-great-horse-profiles" section="use-high-quality-photography" className="ml-auto" />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {previews.filter((p) => p.kind === "photo").map((p) => (
            <ResilientImage key={p.id} src={p.url} alt="" className="w-16 h-16 rounded-xl border border-black/5" imgClassName="rounded-xl" />
          ))}
          <input ref={fileRef} type="file" multiple accept={ACCEPT_PHOTOS} className="hidden" onChange={(e) => handleUpload(e, "photo")} data-testid="horse-photo-input" />
          <button
            type="button"
            data-testid="horse-photo-upload-button"
            onClick={() => fileRef.current?.click()}
            disabled={uploadPct !== null}
            className="w-16 h-16 rounded-xl border border-dashed border-black/15 flex flex-col items-center justify-center text-[#0A0A0A]/40 hover:text-[#0A0A0A] hover:border-black/30 transition-colors duration-200"
            aria-label={t("horse.addPhotos")}
          >
            {uploadPct !== null ? (
              <span className="text-[10px] font-semibold">{uploadPct}%</span>
            ) : (
              <ImagePlus className="w-5 h-5" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-3" data-testid="horse-videos-section">
        <div className="flex items-center gap-2">
          <Label>{t("horse.videosSection")}</Label>
          <span className="text-[11px] text-[#737373]">{t("horse.optionalSection")}</span>
          <LearnMore slug="creating-great-horse-profiles" section="upload-professional-videos" className="ml-auto" />
        </div>
        <VideoManager
          value={videos}
          onChange={setVideos}
          mainId={mainVideoId}
          onMainChange={setMainVideoId}
          onUploadsBusy={setVideoUploadsBusy}
        />
      </div>

      {!compact && (
        <div className="flex flex-col gap-3" data-testid="horse-documents-section">
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <Label>{t("horse.docsSection")}</Label>
              <span className="text-[11px] text-[#737373]">{t("horse.optionalSection")}</span>
              <LearnMore slug="creating-great-horse-profiles" section="be-transparent" className="ml-auto" />
            </div>
            <span className="text-xs text-[#737373]">{t("horse.docsHint")}</span>
          </div>
          {docs.length > 0 && (
            <ul className="rounded-2xl border border-black/5 overflow-hidden divide-y divide-black/5 bg-white">
              {docs.map((d) => (
                <li key={d.id} className="flex items-center gap-3 px-4 py-3" data-testid={`horse-doc-row-${d.id}`}>
                  <FileText className="w-[18px] h-[18px] text-[#0A0A0A]/40 shrink-0" aria-hidden="true" />
                  <span className="text-sm font-medium text-[#0A0A0A] truncate min-w-0 flex-1">{d.name}</span>
                  <Select value={d.category || "general"} onValueChange={(v) => setDocCategory(d.id, v)}>
                    <SelectTrigger
                      data-testid={`horse-doc-category-${d.id}`}
                      aria-label={t("med.category")}
                      className="h-8 w-auto rounded-full bg-black/5 text-[#0A0A0A] text-xs px-3 border-0 shadow-none gap-1 shrink-0 focus:ring-0"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      {DOC_CATEGORIES.map((k) => (
                        <SelectItem key={k} value={k}>
                          {t(`doccat.${k}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button
                    type="button"
                    data-testid={`horse-doc-remove-${d.id}`}
                    aria-label={t("horse.removeDoc")}
                    onClick={() => removeDoc(d.id)}
                    className="h-8 w-8 rounded-full flex items-center justify-center text-[#0A0A0A]/30 hover:text-red-700 hover:bg-red-50 transition-colors duration-200 shrink-0"
                  >
                    <Trash2 className="w-4 h-4" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <input ref={docRef} type="file" multiple accept={ACCEPT_DOCS} className="hidden" onChange={handleDocUpload} data-testid="horse-doc-input" />
          <button
            type="button"
            data-testid="horse-doc-upload-button"
            onClick={() => docRef.current?.click()}
            className="w-fit inline-flex items-center gap-2 text-sm font-medium text-[#0A0A0A]/60 hover:text-[#0A0A0A] transition-colors duration-200"
          >
            <Plus className="w-4 h-4" aria-hidden="true" />
            {t("horse.uploadDoc")}
          </button>
        </div>
      )}

      {!compact && (
        <div className="flex flex-col gap-3" data-testid="horse-vet-section">
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <Label>{t("vet.repository")}</Label>
              <span className="text-[11px] text-[#737373]">{t("horse.optionalSection")}</span>
              <LearnMore slug="creating-great-horse-profiles" section="veterinary-documentation" className="ml-auto" />
            </div>
            <span className="text-xs text-[#737373]">{t("vet.repositoryHint")}</span>
          </div>
          <input ref={vetRef} type="file" multiple accept={ACCEPT_DOCS_AND_VIDEO} className="hidden" onChange={handleVetUpload} data-testid="horse-vet-input" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {VET_CATEGORIES.map((cat) => {
              const items = vetItems.filter((v) => v.vet_category === cat);
              return (
                <div key={cat} className="rounded-2xl border border-black/5 bg-[#F9F9F9] p-4 flex flex-col gap-2" data-testid={`vet-category-${cat}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold uppercase tracking-[0.15em] text-[#0A0A0A]/50">{t(`vet.${cat}`)}</span>
                    {items.length > 0 && (
                      <span className="text-[11px] font-semibold text-[#1D4D3B] tabular-nums">{items.length}</span>
                    )}
                  </div>
                  {items.map((v) => (
                    <div key={v.id} className="flex items-center gap-2 rounded-xl border border-black/5 bg-white px-3 py-2" data-testid={`vet-item-${v.id}`}>
                      <FileText className="w-4 h-4 text-black/30 shrink-0" aria-hidden="true" />
                      <span className="text-[11px] font-medium text-[#0A0A0A] truncate min-w-0 flex-1">{v.name}</span>
                      <button
                        type="button"
                        data-testid={`vet-item-remove-${v.id}`}
                        aria-label={t("horse.removeDoc")}
                        onClick={() => removeVetItem(v.id)}
                        className="h-7 w-7 rounded-full flex items-center justify-center text-[#0A0A0A]/30 hover:text-red-700 hover:bg-red-50 transition-colors duration-200 shrink-0"
                      >
                        <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    data-testid={`vet-upload-${cat}`}
                    onClick={() => {
                      vetCategoryRef.current = cat;
                      vetRef.current?.click();
                    }}
                    className="w-fit inline-flex items-center gap-2 text-xs font-medium text-[#0A0A0A]/50 hover:text-[#0A0A0A] transition-colors duration-200"
                  >
                    <Plus className="w-3.5 h-3.5" aria-hidden="true" />
                    {t("vet.upload")}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {error && (
        <p data-testid="horse-form-error" role="alert" className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
          {error}
        </p>
      )}

      <div className="flex items-center gap-3 pt-2">
        <Button
          type="submit"
          data-testid="horse-form-submit"
          disabled={saving || !form.name || videoUploadsBusy}
          className="rounded-full h-11 px-7 bg-[#0A0A0A] text-white hover:bg-black/80 transition-colors duration-300 disabled:opacity-50"
        >
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />}
          {t("horse.save")}
        </Button>
        {videoUploadsBusy && (
          <span className="text-xs text-[#737373]" data-testid="horse-form-uploads-busy">
            {t("vm.waitUploads")}
          </span>
        )}
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel} data-testid="horse-form-cancel" className="rounded-full h-11 px-6">
            {t("horse.cancel")}
          </Button>
        )}
      </div>

      <PedigreeReviewDialog open={reviewOpen} onOpenChange={setReviewOpen} result={aiResult} onApply={handleAiApply} />
    </form>
  );
};
